import React from "react";
import { useTranslation } from "react-i18next";
import { map } from "lodash";
import { DealStage } from "@/pages/deals/types";
import { Diamond, Guardrails, Workflow } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import DraggableStageRow from "@/pages/settings/components/shared/stageStudio/DraggableStageRow";
import CountBadge from "@/pages/settings/components/shared/stageStudio/CountBadge";

interface Props {
  item: DealStage;
  index: number;
  tPrefix: string;
  isDragDisabled?: boolean;
  isLast?: boolean;
}

const DealStageRow: React.FC<Props> = ({
  item,
  index,
  tPrefix,
  isDragDisabled,
  isLast
}) => {
  const { t } = useTranslation();
  const questionnaires = item.questionnaires || [];
  const numOfGuardrails = questionnaires.filter((q) => q.isGuardrails).length;
  const dealsCount = item.dealsCount || 0;

  const badges = (
    <>
      <Tooltip>
        <TooltipTrigger>
          <CountBadge
            count={questionnaires.length}
            icon={<Workflow />}
            translationKey="workflow"
            tPrefix={tPrefix}
          />
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-2">
            <Text variant="smBodySecondary">
              {t(`${tPrefix}.workflow`, { count: questionnaires.length })}
            </Text>
            <div className="flex flex-col gap-1">
              {map(questionnaires, (q) => (
                <div className="flex gap-2" key={q.id}>
                  <IconContainer className="size-[18px]" color="secondary">
                    <Workflow />
                  </IconContainer>
                  <Text>{q.name}</Text>
                </div>
              ))}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
      <CountBadge
        count={numOfGuardrails}
        icon={<Guardrails />}
        translationKey="guardrail"
        tPrefix={tPrefix}
      />
      <CountBadge
        count={dealsCount}
        icon={<Diamond />}
        translationKey="deal"
        tPrefix={tPrefix}
      />
    </>
  );

  return (
    <DraggableStageRow
      item={{ id: item.id, name: item.name }}
      index={index}
      isDragDisabled={isDragDisabled}
      isLast={isLast}
      color={getColorForMilestoneByType(item.milestone, "border")}
      badges={badges}
    />
  );
};

export default DealStageRow;
